import { useState, useRef } from 'react';
import {
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Card,
  Image as MantineImage,
  Badge,
  Tooltip,
  Progress,
  NumberInput,
  Switch,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconArrowUp,
  IconArrowDown,
  IconStar,
  IconStarOff,
  IconPhoto,
  IconUpload,
  IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

interface ImageManagerProps {
  images: string[];
  onChange: (images: string[]) => void;
  thumbnailIndex?: number;
  onThumbnailChange?: (index: number | undefined) => void;
}

interface ImageInfo {
  url: string;
  originalSize?: number;
  processedSize?: number;
  originalDimensions?: { width: number; height: number };
  processedDimensions?: { width: number; height: number };
  compressed?: boolean;
}

export function ImageManager({
  images,
  onChange,
  thumbnailIndex,
  onThumbnailChange,
}: ImageManagerProps) {
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [imageInfo, setImageInfo] = useState<Record<number, ImageInfo>>({});
  const [uploading, setUploading] = useState<Record<number, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const [compressSettings, setCompressSettings] = useState({
    enabled: true,
    maxWidth: 2000,
    maxHeight: 2000,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const updateImage = (index: number, value: string) => {
    const newImages = [...images];
    newImages[index] = value;
    onChange(newImages);

    // Validate URL
    if (value.trim() && !isValidUrl(value)) {
      setImageErrors((prev) => ({ ...prev, [index]: true }));
    } else {
      setImageErrors((prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      }));
    }
  };

  const addImage = () => {
    onChange([...images, '']);
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
    
    // Adjust thumbnail index if needed
    if (thumbnailIndex !== undefined) {
      if (index === thumbnailIndex) {
        onThumbnailChange?.(undefined);
      } else if (index < thumbnailIndex) {
        onThumbnailChange?.(thumbnailIndex - 1);
      }
    }

    // Clean up errors and image info
    setImageErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return Object.fromEntries(
        Object.entries(newErrors).map(([k, v]) => [parseInt(k) > index ? parseInt(k) - 1 : parseInt(k), v])
      );
    });

    setImageInfo((prev) => {
      const newInfo = { ...prev };
      delete newInfo[index];
      return Object.fromEntries(
        Object.entries(newInfo).map(([k, v]) => [parseInt(k) > index ? parseInt(k) - 1 : parseInt(k), v])
      );
    });
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === images.length - 1)
    ) {
      return;
    }

    const newImages = [...images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    onChange(newImages);

    // Move image info
    setImageInfo((prev) => {
      const newInfo = { ...prev };
      const temp = newInfo[index];
      if (temp !== undefined) {
        newInfo[newIndex] = temp;
        delete newInfo[index];
      }
      return newInfo;
    });

    // Adjust thumbnail index
    if (thumbnailIndex !== undefined) {
      if (thumbnailIndex === index) {
        onThumbnailChange?.(newIndex);
      } else if (thumbnailIndex === newIndex) {
        onThumbnailChange?.(index);
      }
    }
  };

  const setThumbnail = (index: number) => {
    if (thumbnailIndex === index) {
      onThumbnailChange?.(undefined);
    } else {
      onThumbnailChange?.(index);
    }
  };

  const hasValidImage = (url: string): boolean => {
    return url.trim() !== '' && isValidUrl(url) && !imageErrors[images.indexOf(url)];
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = (index: number, file: File) => {
    const fileSizeMB = file.size / (1024 * 1024);
    const maxSizeMB = 10; // 10MB max

    if (fileSizeMB > maxSizeMB) {
      notifications.show({
        title: 'File too large',
        message: `File size (${fileSizeMB.toFixed(2)} MB) exceeds maximum (${maxSizeMB} MB)`,
        color: 'red',
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: 'Invalid file type',
        message: 'Please select an image file',
        color: 'red',
      });
      return;
    }

    uploadImage(index, file);
  };

  const uploadImage = async (index: number, file: File) => {
    setUploading((prev) => ({ ...prev, [index]: true }));
    setUploadProgress((prev) => ({ ...prev, [index]: 0 }));

    try {
      // Get auth token
      const { auth } = await import('@/lib/firebase');
      if (!auth) {
        throw new Error('Auth not initialized');
      }
      // Type assertion for auth
      const user = (auth as any).currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('compress', compressSettings.enabled.toString());
      formData.append('maxWidth', compressSettings.maxWidth.toString());
      formData.append('maxHeight', compressSettings.maxHeight.toString());

      // Simulate progress (since we can't track actual upload progress easily)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => ({
          ...prev,
          [index]: Math.min((prev[index] || 0) + 10, 90),
        }));
      }, 100);

      const response = await fetch('/api/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress((prev) => ({ ...prev, [index]: 100 }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();

      // Update image URL
      const newImages = [...images];
      newImages[index] = data.url;
      onChange(newImages);

      // Store image info
      setImageInfo((prev) => ({
        ...prev,
        [index]: {
          url: data.url,
          originalSize: data.originalSize,
          processedSize: data.processedSize,
          originalDimensions: data.originalDimensions,
          processedDimensions: data.processedDimensions,
          compressed: data.compressed,
        },
      }));

      notifications.show({
        title: 'Image uploaded',
        message: data.compressed
          ? `Compressed from ${data.originalSizeMB} MB to ${data.processedSizeMB} MB`
          : 'Image uploaded successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error: any) {
      notifications.show({
        title: 'Upload failed',
        message: error.message || 'Failed to upload image',
        color: 'red',
      });
    } finally {
      setUploading((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      setUploadProgress((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }
  };

  const triggerFileInput = (index: number) => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('data-index', index.toString());
      fileInputRef.current.click();
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={500} mb="xs">
            Product Images
          </Text>
          <Text size="xs" c="dimmed">
            Upload images or paste URLs. Images larger than 5MB will be automatically compressed.
          </Text>
        </div>
        <Group gap="xs">
          <Button
            leftSection={<IconUpload size={16} />}
            variant="light"
            onClick={() => {
              const emptyIndex = images.findIndex((img) => !img.trim());
              if (emptyIndex >= 0) {
                triggerFileInput(emptyIndex);
              } else {
                addImage();
                setTimeout(() => {
                  triggerFileInput(images.length);
                }, 100);
              }
            }}
            size="sm"
          >
            Upload Image
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={addImage}
            size="sm"
          >
            Add URL
          </Button>
        </Group>
      </Group>

      {/* Compression Settings */}
      <Card withBorder padding="sm" radius="md">
        <Group justify="space-between">
          <Text size="sm" fw={500}>Compression Settings</Text>
          <Switch
            label="Auto-compress large images"
            checked={compressSettings.enabled}
            onChange={(e) =>
              setCompressSettings((prev) => ({ ...prev, enabled: e.currentTarget.checked }))
            }
          />
        </Group>
        {compressSettings.enabled && (
          <Group mt="xs" grow>
            <NumberInput
              label="Max Width (px)"
              value={compressSettings.maxWidth}
              onChange={(value) =>
                setCompressSettings((prev) => ({ ...prev, maxWidth: Number(value) || 2000 }))
              }
              min={100}
              max={4000}
            />
            <NumberInput
              label="Max Height (px)"
              value={compressSettings.maxHeight}
              onChange={(value) =>
                setCompressSettings((prev) => ({ ...prev, maxHeight: Number(value) || 2000 }))
              }
              min={100}
              max={4000}
            />
          </Group>
        )}
      </Card>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const index = parseInt(fileInputRef.current?.getAttribute('data-index') || '0');
            handleFileSelect(index, file);
          }
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
      />

      <Stack gap="md">
        {images.map((image, index) => {
          const isValid = hasValidImage(image);
          const isThumbnail = thumbnailIndex === index;
          const isFirst = index === 0;

          return (
            <Card key={index} withBorder padding="md" radius="md">
              <Stack gap="sm">
                <Group align="flex-start" gap="md">
                  {/* Image Preview */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {isValid ? (
                      <MantineImage
                        src={image}
                        alt={`Preview ${index + 1}`}
                        width={120}
                        height={120}
                        fit="cover"
                        radius="md"
                        style={{ border: '1px solid var(--mantine-color-gray-3)' }}
                        onError={() => {
                          setImageErrors((prev) => ({ ...prev, [index]: true }));
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          border: '2px dashed var(--mantine-color-gray-4)',
                          borderRadius: 'var(--mantine-radius-md)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'var(--mantine-color-gray-0)',
                        }}
                      >
                        <IconPhoto size={32} color="var(--mantine-color-gray-5)" />
                      </div>
                    )}
                    {isThumbnail && (
                      <Badge
                        color="yellow"
                        variant="filled"
                        size="sm"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                        }}
                      >
                        Thumbnail
                      </Badge>
                    )}
                  </div>

                  {/* Image URL Input */}
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb="xs">
                      <TextInput
                        placeholder="https://example.com/image.jpg or upload file"
                        value={image}
                        onChange={(e) => updateImage(index, e.target.value)}
                        error={imageErrors[index] ? 'Invalid URL' : undefined}
                        style={{ flex: 1 }}
                        disabled={uploading[index]}
                      />
                      <Tooltip label="Upload image file">
                        <Button
                          leftSection={<IconUpload size={16} />}
                          variant="light"
                          size="sm"
                          onClick={() => triggerFileInput(index)}
                          disabled={uploading[index]}
                          loading={uploading[index]}
                        >
                          Upload
                        </Button>
                      </Tooltip>
                    </Group>

                    {/* Upload Progress */}
                    {uploading[index] && (
                      <Progress value={uploadProgress[index] || 0} size="sm" mb="xs" />
                    )}

                    {/* Image Info */}
                    {imageInfo[index] && isValid && (
                      <Stack gap={4}>
                        <Group gap="md">
                          {imageInfo[index].originalSize && (
                            <Text size="xs" c="dimmed">
                              Size: {formatFileSize(imageInfo[index].processedSize || imageInfo[index].originalSize!)}
                              {imageInfo[index].compressed && imageInfo[index].originalSize && (
                                <span style={{ textDecoration: 'line-through', marginLeft: 4 }}>
                                  {formatFileSize(imageInfo[index].originalSize)}
                                </span>
                              )}
                            </Text>
                          )}
                          {imageInfo[index].processedDimensions && (
                            <Text size="xs" c="dimmed">
                              {imageInfo[index].processedDimensions.width} × {imageInfo[index].processedDimensions.height} px
                              {imageInfo[index].compressed && imageInfo[index].originalDimensions && (
                                <span style={{ textDecoration: 'line-through', marginLeft: 4 }}>
                                  {' '}
                                  {imageInfo[index].originalDimensions.width} × {imageInfo[index].originalDimensions.height}
                                </span>
                              )}
                            </Text>
                          )}
                          {imageInfo[index].compressed && (
                            <Badge size="xs" color="green" variant="light">
                              Compressed
                            </Badge>
                          )}
                        </Group>
                      </Stack>
                    )}

                    {/* Thumbnail indicator */}
                    {!isThumbnail && isFirst && isValid && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Currently used as thumbnail (first image)
                      </Text>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <Group gap={4}>
                    {isValid && (
                      <Tooltip label={isThumbnail ? 'Remove thumbnail' : 'Set as thumbnail'}>
                        <ActionIcon
                          variant={isThumbnail ? 'filled' : 'subtle'}
                          color={isThumbnail ? 'yellow' : 'gray'}
                          onClick={() => setThumbnail(index)}
                        >
                          {isThumbnail ? <IconStar size={16} /> : <IconStarOff size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label="Move up">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => moveImage(index, 'up')}
                        disabled={index === 0 || uploading[index]}
                      >
                        <IconArrowUp size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Move down">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => moveImage(index, 'down')}
                        disabled={index === images.length - 1 || uploading[index]}
                      >
                        <IconArrowDown size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remove image">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeImage(index)}
                        disabled={uploading[index]}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Stack>
            </Card>
          );
        })}

        {images.length === 0 && (
          <Card withBorder padding="xl" radius="md" style={{ textAlign: 'center' }}>
            <Stack gap="xs">
              <IconPhoto size={48} color="var(--mantine-color-gray-5)" style={{ margin: '0 auto' }} />
              <Text c="dimmed">No images added yet</Text>
              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={addImage}
                size="sm"
              >
                Add First Image
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
    </Stack>
  );
}

